import { Box, Button, Flex, Spinner } from "@chakra-ui/react";
import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Rnd, RndDragCallback } from "react-rnd";
import { VIDEO_CALL_RND_Z_INDEX } from "../chakra-provider/chakra-portal-stack";
import colors from "../../../app/colors";
import OpenInFullIcon from "../../icons/open-in-full-icon";
import VideoCallPage from "./video-call-page";
import type { IWebSocketPayloadIncomingCall } from "../websocket-provider/types";

export type { IWebSocketPayloadIncomingCall };

/** Opens the in-app video UI for an existing call; {@link VideoCallPage} loads `call` by id. */
export interface FullScreenVideoCallInput {
  callId: string;
  /**
   * User id for the app bar title when the caller should be shown (e.g. incoming ring).
   * If omitted, `Call.createdUserId` from the query is used.
   */
  presentationUserId?: string;
}

const insetWindowWidth = 300;
const insetWindowHeight = 200;

function parseStoredVideoCallCoordinates(): { x: number; y: number } {
  const fallback = { x: 0, y: 0 };
  try {
    const raw = localStorage.getItem("videoCallCoordinates");
    if (raw == null || raw === "") return fallback;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return fallback;
    const obj = parsed as Record<string, unknown>;
    const x = typeof obj.x === "number" ? obj.x : Number(obj.x);
    const y = typeof obj.y === "number" ? obj.y : Number(obj.y);
    return {
      x: Number.isFinite(x) ? x : 0,
      y: Number.isFinite(y) ? y : 0,
    };
  } catch {
    return fallback;
  }
}

function clampPipPosition(x: number, y: number): { x: number; y: number } {
  const maxX = Math.max(0, window.innerWidth - insetWindowWidth);
  const maxY = Math.max(0, window.innerHeight - insetWindowHeight);
  return {
    x: Math.min(Math.max(x, 0), maxX),
    y: Math.min(Math.max(y, 0), maxY),
  };
}

interface IContextProps {
  fullScreenVideoCall: (input: FullScreenVideoCallInput) => void;
  isCallActive: boolean;
}

interface IRndState {
  isFullScreen?: boolean;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface IActiveSessionState {
  callId: string;
  presentationUserId?: string;
}

const VideoCallContext = React.createContext<IContextProps>({
  fullScreenVideoCall: () => {},
  isCallActive: false,
});

export const VideoCallProvider = ({ children }: PropsWithChildren) => {
  const [session, setSession] = useState<IActiveSessionState | undefined>();

  const fullScreenVideoCall = useCallback((input: FullScreenVideoCallInput) => {
    setSession({
      callId: input.callId,
      presentationUserId: input.presentationUserId,
    });
    setRndState({
      isFullScreen: true,
      width: window.innerWidth,
      height: window.innerHeight,
      x: 0,
      y: 0,
    });
  }, []);

  const endCall = useCallback(() => {
    setSession(undefined);
    setRndState((currentState) => ({
      ...currentState,
      isFullScreen: undefined,
    }));
  }, []);

  const [rndState, setRndState] = React.useState<IRndState>({
    width: 0,
    height: 0,
    x: 0,
    y: 0,
  });

  const handleFullScreen = useCallback(() => {
    setRndState((currentRndState) => {
      const newFullScreenState = !currentRndState.isFullScreen;

      if (newFullScreenState) {
        return {
          isFullScreen: true,
          width: window.innerWidth,
          height: window.innerHeight,
          x: 0,
          y: 0,
        };
      }

      const coordinates = parseStoredVideoCallCoordinates();
      const { x, y } = clampPipPosition(coordinates.x, coordinates.y);

      return {
        isFullScreen: false,
        width: insetWindowWidth,
        height: insetWindowHeight,
        x,
        y,
      };
    });
  }, []);

  useEffect(() => {
    let resizeTimeout: NodeJS.Timeout;

    const handleResize = () => {
      clearTimeout(resizeTimeout);

      resizeTimeout = setTimeout(() => {
        setRndState((currentState) => {
          if (currentState.isFullScreen) {
            return {
              ...currentState,
              width: window.innerWidth,
              height: window.innerHeight,
            };
          }

          const xy = clampPipPosition(currentState.x, currentState.y);

          localStorage.setItem("videoCallCoordinates", JSON.stringify(xy));

          return {
            ...currentState,
            ...xy,
          };
        });
      }, 100);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  const borderRadius = useMemo(
    () => (rndState.isFullScreen ? undefined : "16px"),
    [rndState.isFullScreen],
  );

  const onDragStop: RndDragCallback = useCallback(
    (e, data) => {
      if (rndState.isFullScreen === false) {
        const xy = {
          x: data.x,
          y: data.y,
        };
        setRndState((currentState) => ({
          ...currentState,
          ...xy,
        }));
        localStorage.setItem("videoCallCoordinates", JSON.stringify(xy));
      }
    },
    [rndState.isFullScreen],
  );

  const value = useMemo<IContextProps>(
    () => ({
      fullScreenVideoCall,
      isCallActive: session != null,
    }),
    [fullScreenVideoCall, session],
  );

  return (
    <VideoCallContext.Provider value={value}>
      {children}
      {rndState.isFullScreen != null && (
        <Rnd
          style={{
            borderRadius,
            background: colors.white,
            zIndex: VIDEO_CALL_RND_Z_INDEX,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
          }}
          bounds="window"
          size={{
            width: rndState.width,
            height: rndState.height,
          }}
          position={{
            x: rndState.x,
            y: rndState.y,
          }}
          onDragStop={onDragStop}
          enableResizing={false}
        >
          <Box
            w="full"
            h="full"
            minH={0}
            position="relative"
            display="flex"
            flexDirection="column"
            alignItems="stretch"
          >
            {session ? (
              <VideoCallPage
                callId={session.callId}
                presentationUserId={session.presentationUserId}
                borderRadius={borderRadius}
                endCall={endCall}
                isFullScreen={!!rndState.isFullScreen}
                onMinimize={handleFullScreen}
              />
            ) : (
              <Flex flex="1" align="center" justify="center" minH={0}>
                <Spinner />
              </Flex>
            )}
            {!rndState.isFullScreen && (
              <Button
                position="absolute"
                top="8px"
                right="8px"
                bg={colors.transparent70Percent}
                _hover={{ bg: colors.transparent70Percent }}
                _active={{ bg: colors.transparent70Percent }}
                borderRadius="8px"
                p="8px"
                onClick={handleFullScreen}
              >
                <OpenInFullIcon />
              </Button>
            )}
          </Box>
        </Rnd>
      )}
    </VideoCallContext.Provider>
  );
};

export const useVideoCallProvider = () => useContext(VideoCallContext);
